import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import api, { setAuth, getToken } from "../api";
import { BackupCodes, CodeBox, QrSetup } from "../components/TwoStep.jsx";

/* The "Signal & Silence" sign-in (design/RepIQ-Login-Design.svg), rebuilt as a live page and fully
   tokenised — every colour reads from the theme, so the same artwork recolours per theme. */

const WAVE_D =
  "M110 450 L300 450 L310 440 L320 460 L330 432 L340 468 L350 422 L360 478 L370 408 L380 492 " +
  "L390 392 L400 508 L410 380 L420 520 L430 372 L440 522 L450 382 L460 510 L470 398 L480 494 " +
  "L490 412 L500 478 L510 426 L520 464 L530 438 L540 458 L550 448 L560 450 L740 450";

function Artwork() {
  const ink = { fill: "var(--text)" };
  const muted = { fill: "var(--text-soft)" };
  const faint = { fill: "var(--text-faint)" };
  const accent = { fill: "var(--accent)" };
  const lineInk = { stroke: "var(--text)" };
  const lineAccent = { stroke: "var(--accent)" };
  return (
    <svg viewBox="100 96 660 770" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
      <defs>
        <linearGradient id="lgfade" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" style={{ stopColor: "var(--accent)", stopOpacity: 0 }} />
          <stop offset="22%" style={{ stopColor: "var(--accent)", stopOpacity: 1 }} />
          <stop offset="78%" style={{ stopColor: "var(--accent)", stopOpacity: 1 }} />
          <stop offset="100%" style={{ stopColor: "var(--accent)", stopOpacity: 0 }} />
        </linearGradient>
        <pattern id="lgruler" width="14" height="48" patternUnits="userSpaceOnUse">
          <line x1="0.5" y1="0" x2="0.5" y2="48" style={lineInk} strokeWidth="1" />
        </pattern>
        <path id="lgwave" d={WAVE_D} fill="none" stroke="url(#lgfade)" strokeWidth="2"
          strokeLinecap="round" strokeLinejoin="round" />
      </defs>

      {/* eyebrow labels */}
      <g fontSize="12" letterSpacing="5">
        <text x="120" y="124" style={muted}>CALL · INTELLIGENCE</text>
        <text x="700" y="124" textAnchor="end" style={faint}>FIG. 01 — THE THRESHOLD</text>
      </g>

      {/* wordmark */}
      <text x="118" y="262" fontSize="68" fontWeight="200" letterSpacing="2" style={ink}>
        Rep<tspan style={accent}>IQ</tspan>
      </text>
      <text x="122" y="300" fontSize="16" fontWeight="300" letterSpacing="3" style={muted}>
        The signal in every conversation.
      </text>

      {/* waveform: silence → signal → silence, with echoes */}
      <use href="#lgwave" transform="translate(0,-138)" opacity="0.10" />
      <use href="#lgwave" transform="translate(0,138)" opacity="0.16" />
      <use href="#lgwave" />

      {/* time axis */}
      <g style={lineInk} strokeOpacity="0.18">
        <line x1="110" y1="588" x2="740" y2="588" strokeWidth="1" />
        <line x1="190" y1="583" x2="190" y2="593" />
        <line x1="310" y1="583" x2="310" y2="593" />
        <line x1="430" y1="580" x2="430" y2="596" style={lineAccent} strokeOpacity="0.6" />
        <line x1="550" y1="583" x2="550" y2="593" />
        <line x1="670" y1="583" x2="670" y2="593" />
      </g>
      <g fontSize="11" letterSpacing="2" textAnchor="middle" style={faint}>
        <text x="190" y="612">−2.0</text>
        <text x="310" y="612">−1.0</text>
        <text x="430" y="612" style={accent}>0.00s</text>
        <text x="550" y="612">+1.0</text>
        <text x="670" y="612">+2.0</text>
      </g>

      {/* measured field */}
      <rect x="120" y="700" width="620" height="48" fill="url(#lgruler)" opacity="0.16" />
      <line x1="120" y1="700" x2="740" y2="700" style={lineInk} strokeOpacity="0.12" />

      {/* footnotes */}
      <g fontSize="12" letterSpacing="3">
        <text x="120" y="812" style={muted}>REPIQ.CO.UK</text>
        <text x="120" y="836" style={faint}>OXFORD · 51.7520° N · 1.2577° W</text>
      </g>
    </svg>
  );
}

/* Sign-in is two steps when two-step verification is on (Salem 2026-10-01, after a staff
   mailbox was phished): the password, then the 6-digit code from an authenticator app (or a
   backup code). Managers and admins who haven't set it up yet are walked through setup here
   before they get in. A set-password link lands on the same code step (state.mfa). */
export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const handoff = location.state?.mfa || null;           // from SetPassword
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // password | code | setup | backup
  const [step, setStep] = useState(handoff ? handoff.mfa : "password");
  const [challenge, setChallenge] = useState(handoff?.challenge || "");
  const [who, setWho] = useState(handoff?.name || "");
  const [code, setCode] = useState("");
  const [useBackup, setUseBackup] = useState(false);
  const [setupData, setSetupData] = useState(null);
  const [backupCodes, setBackupCodes] = useState(null);
  const [pending, setPending] = useState(null);          // {access_token, user} held until codes are saved

  const done = (data) => {
    setAuth(data.access_token, data.user);
    navigate(location.state?.from || "/", { replace: true });
  };

  const startSetup = async (ch) => {
    setSetupData(null);
    try { setSetupData(await api.post("/api/auth/login/setup/start", { challenge: ch })); }
    catch (err) { setError(err.message || "Couldn't start setup"); }
  };

  useEffect(() => {
    if (handoff?.mfa === "setup" && handoff.challenge) startSetup(handoff.challenge);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (getToken() && !pending) return <Navigate to="/" replace />;

  const afterPassword = (data) => {
    if (data.mfa === "code" || data.mfa === "setup") {
      setChallenge(data.challenge); setWho(data.name || ""); setCode(""); setUseBackup(false);
      setStep(data.mfa);
      if (data.mfa === "setup") startSetup(data.challenge);
      return;
    }
    done(data);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      afterPassword(await api.post("/api/auth/login", { email, password }));
    } catch (err) {
      setError(err.message || "Login failed");
    } finally { setLoading(false); }
  };

  const verify = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const data = await api.post("/api/auth/login/verify", { challenge, code: code.trim() });
      done(data);
    } catch (err) {
      setError(err.message || "That code didn't work");
      if (/timed out/i.test(err.message || "")) { setStep("password"); setChallenge(""); }
    } finally { setLoading(false); }
  };

  const confirmSetup = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const data = await api.post("/api/auth/login/setup/confirm", { challenge, code: code.trim() });
      setPending({ access_token: data.access_token, user: data.user });
      setBackupCodes(data.backupCodes || []);
      setStep("backup");
    } catch (err) {
      setError(err.message || "That code didn't match");
      if (/timed out/i.test(err.message || "")) { setStep("password"); setChallenge(""); }
    } finally { setLoading(false); }
  };

  const restart = (e) => {
    e?.preventDefault();
    setStep("password"); setChallenge(""); setCode(""); setError(""); setSetupData(null);
    navigate("/login", { replace: true, state: location.state?.from ? { from: location.state.from } : undefined });
  };

  const codeOk = useBackup ? code.replace(/[^a-z0-9]/gi, "").length === 8 : code.replace(/\D/g, "").length === 6;

  return (
    <div className="lg2">
      <span className="lg2-corner tl" /><span className="lg2-corner tr" />
      <span className="lg2-corner bl" /><span className="lg2-corner br" />

      <div className="lg2-art"><Artwork /></div>

      <div className="lg2-panel">
        {step === "password" && (
          <form className="lg2-form" onSubmit={submit}>
            <div className="lg2-eyebrow">SIGN IN</div>
            <h1 className="lg2-title">Welcome back</h1>
            <p className="lg2-sub">Sign in to your RepIQ workspace.</p>

            {error && <div className="lg2-err">{error}</div>}

            <div className="lg2-field">
              <div className="lg2-flabel"><span>Email</span></div>
              <input className="lg2-input" type="email" value={email} autoFocus required
                placeholder="you@btlocalbusiness.co.uk" autoComplete="username"
                onChange={(e) => setEmail(e.target.value)} />
            </div>

            <div className="lg2-field">
              <div className="lg2-flabel">
                <span>Password</span>
                <span className="show" onClick={() => setShow((s) => !s)}>{show ? "HIDE" : "SHOW"}</span>
              </div>
              <input className="lg2-input" type={show ? "text" : "password"} value={password} required
                placeholder="••••••••••" autoComplete="current-password" onChange={(e) => setPassword(e.target.value)} />
            </div>

            <button className="lg2-btn" type="submit" disabled={loading || (!email.trim() || !password)}>
              <span>{loading ? "Signing in…" : "Sign in"}</span><span>→</span>
            </button>

            <a className="lg2-link" href="#" onClick={(e) => e.preventDefault()}
              title="Ask your manager for a reset link">Forgotten your password?</a>

            <div className="lg2-foot">SECURED ACCESS · REPIQ v2</div>
          </form>
        )}

        {step === "code" && (
          <form className="lg2-form" onSubmit={verify}>
            <div className="lg2-eyebrow">TWO-STEP VERIFICATION</div>
            <h1 className="lg2-title">{who ? `Hi ${who}` : "One more step"}</h1>
            <p className="lg2-sub">
              {useBackup ? "Enter one of your backup codes. Each code works once."
                : "Enter the 6-digit code from your authenticator app."}
            </p>
            {error && <div className="lg2-err">{error}</div>}
            <div className="lg2-field">
              <div className="lg2-flabel"><span>{useBackup ? "Backup code" : "Code"}</span></div>
              <CodeBox className="lg2-input" value={code} onChange={setCode} backup={useBackup} />
            </div>
            <button className="lg2-btn" type="submit" disabled={loading || !codeOk}>
              <span>{loading ? "Checking…" : "Verify"}</span><span>→</span>
            </button>
            <a className="lg2-link" href="#" onClick={(e) => { e.preventDefault(); setUseBackup((b) => !b); setCode(""); setError(""); }}>
              {useBackup ? "Use my authenticator app instead" : "Lost your phone? Use a backup code"}
            </a>
            <div className="lg2-sub" style={{ marginTop: 14, fontSize: 11.5 }}>
              No phone and no backup codes? Ask your manager to reset your two-step verification.
            </div>
            <a className="lg2-link" href="#" onClick={restart} style={{ marginTop: 12 }}>← Back to sign in</a>
            <div className="lg2-foot">SECURED ACCESS · REPIQ v2</div>
          </form>
        )}

        {step === "setup" && (
          <form className="lg2-form" onSubmit={confirmSetup}>
            <div className="lg2-eyebrow">SECURE YOUR ACCOUNT</div>
            <h1 className="lg2-title" style={{ fontSize: 27 }}>Set up two-step verification</h1>
            <p className="lg2-sub" style={{ marginBottom: 14 }}>
              {who ? `${who}, your` : "Your"} role needs a second step at sign-in, so a stolen password alone can't open RepIQ. It takes a minute.
            </p>
            {error && <div className="lg2-err">{error}</div>}
            <div className="tfa-panel"><QrSetup data={setupData} /></div>
            <div className="lg2-field">
              <div className="lg2-flabel"><span>6-digit code</span></div>
              <CodeBox className="lg2-input" value={code} onChange={setCode} autoFocus={false} />
            </div>
            <button className="lg2-btn" type="submit" disabled={loading || !codeOk || !setupData}>
              <span>{loading ? "Checking…" : "Turn on & continue"}</span><span>→</span>
            </button>
            <a className="lg2-link" href="#" onClick={restart}>← Back to sign in</a>
          </form>
        )}

        {step === "backup" && (
          <div className="lg2-form">
            <div className="lg2-eyebrow">ALMOST DONE</div>
            <h1 className="lg2-title" style={{ fontSize: 27 }}>Two-step verification is on</h1>
            <p className="lg2-sub" style={{ marginBottom: 14 }}>From now on you'll enter a code from your app after your password.</p>
            <div className="tfa-panel"><BackupCodes codes={backupCodes || []} email={pending?.user?.email} /></div>
            <button className="lg2-btn" type="button" onClick={() => pending && done(pending)}>
              <span>I've saved them — continue</span><span>→</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
